import { timestampDate } from '@bufbuild/protobuf/wkt'
import { BiddingApprovalState } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type { BiddingSpotApprovalRequest } from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  HStack,
  PrimaryButton,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { AlertActions } from '~/components/common/AlertActions'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import type { ApprovalBannerEntry } from '~/pages/SpotBidding/components/ApprovalBannerStack'
import { ApprovalBannerStack } from '~/pages/SpotBidding/components/ApprovalBannerStack'
import { ApprovalDecisionActions } from '~/pages/SpotBidding/components/ApprovalDecisionActions'
import { ApproverIdentityNotice } from '~/pages/SpotBidding/components/ApproverIdentityNotice'
import { useApproverIdentity } from '~/pages/SpotBidding/hooks/useApproverIdentity'
import type { ApprovalBannerHeading } from '~/pages/SpotBidding/models/approvalBannerHeading'
import {
  APPROVAL_BANNER_ALERT_STATUS,
  APPROVAL_BANNER_TITLE_KEY,
  resolveApprovalBannerHeading,
} from '~/pages/SpotBidding/models/approvalBannerHeading'
import type { ApproverIdentityResolver } from '~/pages/SpotBidding/models/approverIdentity'
import type { SpotMarketPhase } from '~/pages/SpotBidding/models/marketPhase'
import { spotAcceptanceOpenAtLabel } from '~/pages/SpotBidding/models/marketPhase'
import { toMatchOutcome } from '~/pages/SpotBidding/models/spotBidStatusCategory'

type Props = {
  /** 画面の市場フェーズ。 承認済の見出しが入札締切の前後で分かれる (AC-BSP-067)。 */
  phase: SpotMarketPhase
  /** 受渡日。 予約入札フェーズの見出しに受付開始日時を出す。 */
  targetDate: Date
  /**
   * 受渡日の全承認案件 (= 追加入札 AC-079 により同一受渡日に複数申請セットが
   * 並存しうる、 AC-082)。 未作成 (0 件) は空配列。
   */
  approvalRequests: BiddingSpotApprovalRequest[]
  /**
   * PENDING の申請セットに「申請取消」ボタンを出し、押下時に対象 request を渡す
   * (AC-055、 = 申請者が下書きに戻す自己リカバリ経路)。 省略時は取消ボタンを
   * 描画しない (= read-only 表示、 AC-058 の確定済ワークフロー経由もこれで塞ぐ)。
   * 実 CANCEL RPC・確認 modal は呼び出し元 (ページ) の責務。
   *
   * 本人検証 (AC-055b) と締切検証 (AC-055c) は backend 未実装のため、 現状は同一
   * organization のメンバーなら誰の申請でも取り消せる (逸脱の経緯は呼び出し元
   * SpotBidding/index.tsx の handleCancelRequest コメント + issue #4087)。
   */
  onCancelRequest?: (request: BiddingSpotApprovalRequest) => void
  /**
   * REJECTED の申請セットに「再編集して再申請」ボタンを出し、押下時に対象 request を
   * 渡す (AC-056、 = 差戻し後に申請者が下書きに戻して再申請する経路)。 省略時は
   * 再編集ボタンを描画しない (= read-only 表示)。 実 REOPEN RPC は呼び出し元 (ページ)
   * の責務で、 確認 modal は挟まない (= 下書きに戻すだけの可逆操作。 design
   * `spot.html:10787`)。 本人検証・締切検証は backend が担う (AC-056b/c)。
   */
  onReopenRequest?: (request: BiddingSpotApprovalRequest) => void
  /**
   * PENDING の申請セットに「承認」ボタンを出し、 押下時に対象 request を渡す
   * (AC-BSP-094 = 承認者の承認経路。 承認案件一覧画面が Phase 1 時点で動作保証
   * 対象外のため、 本画面が承認操作の唯一の経路)。 省略時は承認ボタンを描画しない。
   * 実 APPROVE RPC・確認 modal は呼び出し元 (ページ) の責務。
   *
   * 承認者でない人・申請者本人にも押せるまま出し、 近くに注意を出す (AC-BSP-098)。
   *
   * 完了済みワークフロー (AC-BSP-058) / 明細未解決 の間は `decisionDisabledReason` で
   * 非活性にする (= この prop は省略せず渡す)。 承認待ちのバナーは入札締切後に出ない
   * (AC-BSP-067) ので、 締切 (AC-BSP-099) の成分が効くのはモーダルを開いたまま締切を
   * 跨いだ時の確定時の再判定。 再判定も同 hook が持つ。
   */
  onApproveRequest?: (request: BiddingSpotApprovalRequest) => void
  /**
   * 承認 / 差戻し を非活性にする理由 (= hover tooltip)。 undefined なら活性。 出し方の
   * 根拠は `ApprovalDecisionActions` の JSDoc。 値は `useSpotApprovalDecision` の
   * `decisionDisabledReason` (= 確定時ガードと同じ policy) をそのまま渡す。
   */
  decisionDisabledReason?: string
  /**
   * PENDING の申請セットに「差戻し」ボタンを出し、 押下時に対象 request を渡す
   * (AC-BSP-094)。 省略時は差戻しボタンを描画しない。 差戻し理由の入力 (必須、
   * AC-BSP-063) と実 REJECT RPC は呼び出し元 (ページ) の責務。
   */
  onRejectRequest?: (request: BiddingSpotApprovalRequest) => void
  /**
   * PENDING の申請セットに「対象の札を確認」導線を出し、 押下時に対象 request を渡す
   * (= 入札一覧をこの申請セットの札だけに絞り込ませる)。 省略時は描画しない。
   *
   * 承認前に承認者が対象の札一覧を確認できることはマスト要件 (2026-08-26 Des 判断)。
   * 確認 modal のサマリ (件数・売買数量・違反明細) だけでは個々の札を確認できず、
   * かつ AC-BSP-079 / AC-BSP-082 が同一受渡日での申請セット並存を許すため、
   * バナーだけでは「どの札の承認案件か」 が判別できない (= 差戻し後再編集 AC-BSP-056 で
   * 既に同じ判断をしている)。
   */
  onReviewRequest?: (request: BiddingSpotApprovalRequest) => void
}

/**
 * 承認状態別のステータスバナー。
 *
 * 見出しの写像は [[resolveApprovalBannerHeading]] (AC-BSP-067)。 表示する監査情報:
 *   - PENDING: 申請者 (requestedBy) / 申請日時 (requestedAt) / 件数。 差戻し後の
 *     再申請 (rejected_* 保持) は前回差戻しコンテキストも併記 (AC-056 / spec 367-368)
 *   - APPROVED / SUBMITTED (承認済・入札中): 承認者 (approvedBy) / 承認日時
 *     (approvedAt) / 申請日時 / 件数。 SUBMITTED は提出日時 (submittedAt) も
 *   - REJECTED: 差戻し理由 (rejectedReason) / 差戻し日時 (rejectedAt)
 *   - DRAFT (REOPEN 後で rejected_* 保持): 前回差戻しコンテキスト (AC-056 / spec 367-368)
 *
 * AC-082: 同一受渡日に複数の承認案件が並存しうるため、 UNSPECIFIED / FAILED /
 * CANCELLED と差戻し履歴の無い純粋な DRAFT を除く各 request についてバナーを縦積みで
 * 表示する (= 暫定実装。 承認状態の異なる札が混在する場合の見せ方は Des 判断範囲、
 * spec spot-bidding.md AC-082 参照)。
 */
export const ApprovalStatusBanner = ({
  phase,
  targetDate,
  approvalRequests,
  onCancelRequest,
  onReopenRequest,
  onApproveRequest,
  onRejectRequest,
  onReviewRequest,
  decisionDisabledReason,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const resolveDisplayName = useMemberDisplayName()
  const approverIdentity = useApproverIdentity()

  // req と banner の対応を保ったまま渡す (= element 配列に潰すと どの案件がどの状態か
  // 分からなくなり、 優先度も集約も書けない)。 並べ替え・畳み込みは共通部品の責務。
  // バナー要素は ApprovalBannerStack が表示する分だけ作る (render 関数を渡す)。 バナーを
  // 出さない案件はここで除く (= 3 件の枠や「残り N 件」 の数に含めない)。
  // 出すかの判断と見出しは 1 回だけ決め、 render へはその結果を渡す。
  const entries: ApprovalBannerEntry[] = approvalRequests.flatMap((req) => {
    const kind = bannerKindOf(req, phase)
    if (kind === undefined) {
      return []
    }
    return [
      {
        id: req.id,
        approvalState: req.approvalState,
        render: () =>
          renderBannerForRequest(
            req,
            kind,
            t,
            {
              onCancelRequest,
              onReopenRequest,
              onApproveRequest,
              onRejectRequest,
              onReviewRequest,
              decisionDisabledReason,
              resolveDisplayName,
              approverIdentity,
            },
            { phase, targetDate },
          ),
      },
    ]
  })

  return <ApprovalBannerStack entries={entries} />
}

/** バナーの種類。 見出し (AC-BSP-067) か、 差戻し後に再編集した下書きのバナー。 */
type BannerKind = ApprovalBannerHeading | 'reopenedDraft'

/**
 * 案件のバナーの種類を決める。 undefined はバナーを出さない (= 3 件の枠や「残り N 件」 に
 * 数えない)。 差戻し履歴の無い純粋な下書きと、 見出しを決められない依頼
 * (UNSPECIFIED / FAILED / CANCELLED、 承認なし・自動のエリアの依頼) は出さない。
 */
function bannerKindOf(
  approvalRequest: BiddingSpotApprovalRequest,
  phase: SpotMarketPhase,
): BannerKind | undefined {
  if (approvalRequest.approvalState === BiddingApprovalState.DRAFT) {
    const hasPriorRejection =
      approvalRequest.rejectedReason !== undefined ||
      approvalRequest.rejectedAt !== undefined
    return hasPriorRejection ? 'reopenedDraft' : undefined
  }
  return resolveApprovalBannerHeading({
    approvalMode: approvalRequest.approvalMode,
    approvalState: approvalRequest.approvalState,
    sendStatus: approvalRequest.sendStatus,
    phase,
    matchOutcomes: approvalRequest.spotBids.map((bid) =>
      toMatchOutcome(bid.bidStatus),
    ),
  })
}

/** バナー上の操作導線 (= 省略された導線はボタンを描画しない) と、 承認/差戻しの非活性理由。 */
type BannerHandlers = Pick<
  Props,
  | 'onCancelRequest'
  | 'onReopenRequest'
  | 'onApproveRequest'
  | 'onRejectRequest'
  | 'onReviewRequest'
  | 'decisionDisabledReason'
> & {
  resolveDisplayName: (memberId: string | undefined) => string | undefined
  approverIdentity: ApproverIdentityResolver
}

/** 1 件の承認案件から、 `bannerKindOf` が決めた種類の承認状態バナーを組み立てる。 */
function renderBannerForRequest(
  approvalRequest: BiddingSpotApprovalRequest,
  kind: BannerKind,
  t: TFunction,
  handlers: BannerHandlers,
  context: Pick<Props, 'phase' | 'targetDate'>,
): React.ReactElement {
  const {
    onCancelRequest,
    onReopenRequest,
    onApproveRequest,
    onRejectRequest,
    onReviewRequest,
    decisionDisabledReason,
    resolveDisplayName,
    approverIdentity,
  } = handlers
  const { approvalState } = approvalRequest

  // DRAFT: 差戻し後に再編集 (REOPEN) された下書きは rejected_* が保持されており
  // (AC-056)、 再申請時に前回差戻しのコンテキスト (日時・理由) をステータスバー内に
  // 表示する (spec spot-bidding.md line 367-368)。 差戻し履歴の無い純粋な下書きは
  // 親が承認ボタン出し分けで制御するためバナー不要 (= `bannerKindOf` が除く)。
  if (kind === 'reopenedDraft') {
    return renderReopenedDraftBanner(approvalRequest, t)
  }
  const heading = kind

  // PENDING
  if (heading === 'pending') {
    const requestedAtDate =
      approvalRequest.requestedAt !== undefined
        ? timestampDate(approvalRequest.requestedAt)
        : undefined
    return (
      // 申請中は warning。spec `spot-bidding.md` の承認ウィザード
      // 「申請中（warning）: 申請日時・申請者・承認者・件数を表示」、design
      // `bidding/spot.html` の承認パネルも `.ecl-alert--warning` で一致する。
      // info だと「承認者の判断待ちで止まっている」状態が単なる案内と同じ
      // 見え方になり、他の待ち状態バナー (差戻し後の下書き = warning) とも
      // 揃わない。
      <Alert
        key={approvalRequest.id}
        description={
          <>
            <HStack flexWrap="wrap" gap={4}>
              {approvalRequest.requestedBy !== undefined && (
                <Text>
                  {t('page:biddingSpot.approvalBanner.requestedBy')}:{' '}
                  {resolveDisplayName(approvalRequest.requestedBy) ?? '—'}
                </Text>
              )}
              {requestedAtDate !== undefined && (
                <Text>
                  {t('page:biddingSpot.approvalBanner.requestedAt')}:{' '}
                  {requestedAtDate.toLocaleString('ja-JP', {
                    timeZone: 'Asia/Tokyo',
                  })}
                </Text>
              )}
              {/* 件数 (spec `spot-bidding.md:505`「申請日時・申請者・承認者・件数を表示」)。
                  数えるのは**この承認案件に紐づく札**なので `approvalRequest.spotBids` を使う。
                  画面スコープの一覧を数えると、 承認案件が複数並ぶときに全案件の合計が
                  どのバナーにも出てしまう (2026-08-25)。 */}
              <Text>
                {t('page:biddingSpot.approvalBanner.bidCount')}:{' '}
                {t('page:biddingSpot.approvalBanner.bidCountValue', {
                  count: approvalRequest.spotBids.length,
                })}
              </Text>
              {/* 承認前に対象の札そのものを確認するための導線 (2026-08-26 Des 判断
                  「承認者が札一覧を確認できることはマスト要件」)。 件数の直後に置くのは、
                  この件数が指す中身を開く操作だから。 右側のボタン群 (申請取消 / 差戻し /
                  承認) は「この案件をどうするか」 の決定操作なので混ぜない。 */}
              {/* **枠線のある variant を使う。** `TextButton` は DS 上 border も
                  背景も持たないため、alert の地色の上では本文と同じ「文字」に見えて
                  押せる導線だと分からない (2026-09-02 デザイナー指摘)。
                  `design/CLAUDE.md` も「Tertiary ≠ Text。Tertiary は border あり、
                  Text は border なし」「素のテキストリンクは status 色と同色で
                  リンクだと分からない」と定めている。
                  8080 の `.ecl-alert` 内の操作も例外なく btn-tertiary / btn-secondary /
                  btn-outline-danger で、btn-text は「デモ」ラベル付きの検証用のみ。 */}
              {onReviewRequest !== undefined && (
                <TertiaryButton
                  onClick={() => {
                    onReviewRequest(approvalRequest)
                  }}
                  size="sm"
                >
                  {t('page:biddingSpot.action.reviewBids')}
                </TertiaryButton>
              )}
            </HStack>
            {(onApproveRequest !== undefined ||
              onRejectRequest !== undefined) && (
              <ApproverIdentityNotice
                identity={approverIdentity(approvalRequest)}
              />
            )}
            {/*
             * 差戻し後に再申請した PENDING は rejected_* を保持する (backend の
             * RequestApproval は rejected_* を消さない)。 承認者が前回差戻しを
             * 把握できるよう、前回差戻しコンテキスト (理由・日時) をステータスバー
             * 内に併記する (AC-056 / spec spot-bidding.md line 367-368)。
             */}
            {renderPriorRejectionSection(approvalRequest, t)}
          </>
        }
        status={APPROVAL_BANNER_ALERT_STATUS.pending}
        title={t(APPROVAL_BANNER_TITLE_KEY.pending)}
      >
        {/* 右端の操作群 (申請取消 / 差戻し / 承認)。 並び・variant・無効化の見せ方は
            共通部品 ApprovalDecisionActions の JSDoc。 AC-BSP-055 の申請取消は呼び出し元が
            導線ごと省略し、 AC-BSP-094 の承認/差戻しは decisionDisabledReason で
            非活性にする。 入札締切後は承認待ち・差戻しのバナー自体が出ない
            (AC-BSP-067)。 */}
        <AlertActions>
          <ApprovalDecisionActions
            decisionDisabledReason={decisionDisabledReason}
            labels={{
              cancel: t('page:biddingSpot.action.cancel'),
              reject: t('page:biddingSpot.action.reject'),
              approve: t('page:biddingSpot.action.approve'),
            }}
            onApprove={
              onApproveRequest === undefined
                ? undefined
                : () => {
                    onApproveRequest(approvalRequest)
                  }
            }
            onCancel={
              onCancelRequest === undefined
                ? undefined
                : () => {
                    onCancelRequest(approvalRequest)
                  }
            }
            onReject={
              onRejectRequest === undefined
                ? undefined
                : () => {
                    onRejectRequest(approvalRequest)
                  }
            }
          />
        </AlertActions>
      </Alert>
    )
  }

  // REJECTED
  if (heading === 'rejected') {
    const rejectedAtDate =
      approvalRequest.rejectedAt !== undefined
        ? timestampDate(approvalRequest.rejectedAt)
        : undefined
    return (
      <Alert
        key={approvalRequest.id}
        description={
          approvalRequest.rejectedReason !== undefined ||
          rejectedAtDate !== undefined ? (
            <VStack align="stretch" gap={1}>
              {approvalRequest.rejectedReason !== undefined && (
                <Text>
                  {t('page:biddingSpot.approvalBanner.rejectedReason')}:{' '}
                  {approvalRequest.rejectedReason}
                </Text>
              )}
              {rejectedAtDate !== undefined && (
                <Text>
                  {t('page:biddingSpot.approvalBanner.rejectedAt')}:{' '}
                  {rejectedAtDate.toLocaleString('ja-JP', {
                    timeZone: 'Asia/Tokyo',
                  })}
                </Text>
              )}
            </VStack>
          ) : undefined
        }
        status={APPROVAL_BANNER_ALERT_STATUS.rejected}
        title={t(APPROVAL_BANNER_TITLE_KEY.rejected)}
      >
        {/*
         * AC-056: 差戻し後に申請者が下書きに戻して再申請する経路。
         * 差戻しバナー上ではこれが**その状態でユーザーが取るべき唯一の操作**
         * なので PrimaryButton (2026-08-17 デザイナー指摘、issue #5147)。
         * 旧実装は「戻す」系の副次操作と解釈して TertiaryButton にしていたが、
         * 差し戻された札は再申請しない限り前に進まないため、副次ではなく主操作。
         * 押下で呼び出し元 (ページ) が即 REOPEN する (= 確認 modal は挟まない、
         * design `spot.html:10787`)。 押下後は入札一覧が対象申請セットの札に
         * 絞り込まれてスクロールする。
         */}
        {onReopenRequest !== undefined && (
          <AlertActions>
            <PrimaryButton
              onClick={() => {
                onReopenRequest(approvalRequest)
              }}
              size="sm"
            >
              {t('page:biddingSpot.action.reopen')}
            </PrimaryButton>
          </AlertActions>
        )}
      </Alert>
    )
  }

  // 承認済・入札中 (承認軸の APPROVED / SUBMITTED を送信軸で分けた見出し)。
  // 申請→承認の日時・承認者・件数を出す (spec §機能詳細 6)。
  const approvedAtDate =
    approvalRequest.approvedAt !== undefined
      ? timestampDate(approvalRequest.approvedAt)
      : undefined
  const approvedRequestedAtDate =
    approvalRequest.requestedAt !== undefined
      ? timestampDate(approvalRequest.requestedAt)
      : undefined
  const submittedAtDate =
    approvalState === BiddingApprovalState.SUBMITTED &&
    approvalRequest.submittedAt !== undefined
      ? timestampDate(approvalRequest.submittedAt)
      : undefined
  return (
    <Alert
      key={approvalRequest.id}
      description={
        <HStack flexWrap="wrap" gap={4}>
          {approvalRequest.approvedBy !== undefined && (
            <Text>
              {t('page:biddingSpot.approvalBanner.approvedBy')}:{' '}
              {resolveDisplayName(approvalRequest.approvedBy) ?? '—'}
            </Text>
          )}
          {approvedAtDate !== undefined && (
            <Text>
              {t('page:biddingSpot.approvalBanner.approvedAt')}:{' '}
              {approvedAtDate.toLocaleString('ja-JP', {
                timeZone: 'Asia/Tokyo',
              })}
            </Text>
          )}
          {/* 承認済でも「いつ申請した何件か」 が要る (spec 同上)。 申請日時は
              承認後も残る (backend は approve で requested_* を消さない)。 */}
          {approvedRequestedAtDate !== undefined && (
            <Text>
              {t('page:biddingSpot.approvalBanner.requestedAt')}:{' '}
              {approvedRequestedAtDate.toLocaleString('ja-JP', {
                timeZone: 'Asia/Tokyo',
              })}
            </Text>
          )}
          {submittedAtDate !== undefined && (
            <Text>
              {t('page:biddingSpot.approvalBanner.submittedAt')}:{' '}
              {submittedAtDate.toLocaleString('ja-JP', {
                timeZone: 'Asia/Tokyo',
              })}
            </Text>
          )}
          <Text>
            {t('page:biddingSpot.approvalBanner.bidCount')}:{' '}
            {t('page:biddingSpot.approvalBanner.bidCountValue', {
              count: approvalRequest.spotBids.length,
            })}
          </Text>
        </HStack>
      }
      status={APPROVAL_BANNER_ALERT_STATUS[heading]}
      title={t(APPROVAL_BANNER_TITLE_KEY[heading], {
        openAt: spotAcceptanceOpenAtLabel(context.targetDate),
      })}
    />
  )
}

/**
 * REOPEN 後 (= 差戻し → 下書き) の DRAFT に、 前回差戻しのコンテキスト
 * (差戻し理由・日時) を表示するバナー (AC-056 / spec line 367-368)。
 * `bannerKindOf` が選んだ (= rejected_* を保持する) 下書きだけが届く。
 */
function renderReopenedDraftBanner(
  approvalRequest: BiddingSpotApprovalRequest,
  t: TFunction,
): React.ReactElement {
  const rejectionRows = renderPriorRejectionRows(approvalRequest, t)
  return (
    <Alert
      key={approvalRequest.id}
      description={rejectionRows}
      status="warning"
      title={t('page:biddingSpot.approvalBanner.reopenedDraftTitle')}
    />
  )
}

/**
 * 前回差戻しコンテキスト (差戻し理由・日時) の行。 REOPEN 後の DRAFT
 * (再編集中) と、 再申請後の PENDING (再申請中) の双方でステータスバー内に
 * 併記する (AC-056 / spec line 367-368)。 rejected_* を保持しない場合は null。
 */
function renderPriorRejectionRows(
  approvalRequest: BiddingSpotApprovalRequest,
  t: TFunction,
): React.ReactElement | null {
  const { rejectedReason, rejectedAt } = approvalRequest
  if (rejectedReason === undefined && rejectedAt === undefined) {
    return null
  }
  const rejectedAtDate =
    rejectedAt !== undefined ? timestampDate(rejectedAt) : undefined
  return (
    <VStack align="stretch" gap={1}>
      {rejectedReason !== undefined && (
        <Text>
          {t('page:biddingSpot.approvalBanner.rejectedReason')}:{' '}
          {rejectedReason}
        </Text>
      )}
      {rejectedAtDate !== undefined && (
        <Text>
          {t('page:biddingSpot.approvalBanner.rejectedAt')}:{' '}
          {rejectedAtDate.toLocaleString('ja-JP', {
            timeZone: 'Asia/Tokyo',
          })}
        </Text>
      )}
    </VStack>
  )
}

/**
 * PENDING バナーに併記する前回差戻しブロック (小見出し + 差戻し行)。 rejected_*
 * を保持しない純粋な申請中は null (= 併記しない)。 見出しは DRAFT 版とは状態が
 * 異なる (再申請中 vs 再編集中) ため priorRejectionTitle を用いる。
 */
function renderPriorRejectionSection(
  approvalRequest: BiddingSpotApprovalRequest,
  t: TFunction,
): React.ReactElement | null {
  const rejectionRows = renderPriorRejectionRows(approvalRequest, t)
  if (rejectionRows === null) {
    return null
  }
  return (
    <VStack align="stretch" gap={1} marginTop={2}>
      <Text fontWeight="semibold">
        {t('page:biddingSpot.approvalBanner.priorRejectionTitle')}
      </Text>
      {rejectionRows}
    </VStack>
  )
}
